/**
 * Environment Variable Validation
 * Validates all required environment variables at app startup
 * Throws errors immediately if any are missing to prevent runtime failures
 */

interface EnvVar {
  key: string;
  required: boolean;
  description: string;
}

const REQUIRED_ENV_VARS: EnvVar[] = [
  // AWS Configuration
  {
    key: 'VITE_AWS_REGION',
    required: true,
    description: 'AWS region for services',
  },
  {
    key: 'VITE_COGNITO_USER_POOL_ID',
    required: true,
    description: 'Cognito User Pool ID for authentication',
  },
  {
    key: 'VITE_COGNITO_CLIENT_ID',
    required: true,
    description: 'Cognito Client ID for authentication',
  },

  // AWS Lambda API Configuration
  {
    key: 'VITE_AWS_API_ENDPOINT',
    required: true,
    description: 'AWS Lambda API endpoint for batch processing',
  },

  // Account Service API Configuration
  {
    key: 'VITE_ACCOUNT_SERVICE_URL',
    required: true,
    description: 'Account Service Lambda endpoint',
  },

  // Stripe Configuration
  {
    key: 'VITE_STRIPE_PUBLISHABLE_KEY',
    required: true,
    description: 'Stripe publishable key for payment processing',
  },
  {
    key: 'VITE_STRIPE_API_URL',
    required: true,
    description: 'Stripe Lambda function URL for payment operations',
  },

  // Storage Configuration
  {
    key: 'VITE_S3_BUCKET',
    required: true,
    description: 'S3 bucket name for file uploads',
  },
  {
    key: 'VITE_S3_REGION',
    required: true,
    description: 'S3 bucket region',
  },

  // Application Configuration
  {
    key: 'VITE_APP_URL',
    required: true,
    description: 'Application URL for redirects',
  },
];

export class EnvironmentValidationError extends Error {
  missingVars: string[];

  constructor(
    message: string,
    missingVars: string[]
  ) {
    super(message);
    this.name = 'EnvironmentValidationError';
    this.missingVars = missingVars;
  }
}

/**
 * Validates all required environment variables
 * @throws {EnvironmentValidationError} If any required variables are missing
 */
export function validateEnvironment(): void {
  const missingVars: string[] = [];
  const errors: string[] = [];

  for (const envVar of REQUIRED_ENV_VARS) {
    if (!envVar.required) continue;

    const value = import.meta.env[envVar.key];

    if (!value || (typeof value === 'string' && value.trim().length === 0)) {
      missingVars.push(envVar.key);
      errors.push(`  ❌ ${envVar.key}: ${envVar.description}`);
    }
  }

  if (missingVars.length > 0) {
    const errorMessage = `
╔═══════════════════════════════════════════════════════════════════════╗
║                    ENVIRONMENT CONFIGURATION ERROR                     ║
╚═══════════════════════════════════════════════════════════════════════╝

The following required environment variables are missing or empty:

${errors.join('\n')}

Please ensure all required environment variables are set in your .env file.
See .env.example for reference.

Application startup has been blocked to prevent runtime errors.
`;

    throw new EnvironmentValidationError(errorMessage, missingVars);
  }

  // Log successful validation in development
  if (import.meta.env.DEV) {
    console.log('✅ Environment variables validated successfully');
  }
}

/**
 * Validates a specific environment variable and returns it
 * @throws {Error} If the variable is missing
 */
export function requireEnv(key: string): string {
  const value = import.meta.env[key];

  if (!value || (typeof value === 'string' && value.trim().length === 0)) {
    throw new Error(
      `Required environment variable ${key} is missing. Please check your .env file.`
    );
  }

  return value;
}

/**
 * Gets an environment variable with a default fallback
 */
export function getEnv(key: string, defaultValue: string): string {
  const value = import.meta.env[key];
  return value && typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : defaultValue;
}
